import styled from 'styled-components';
import {
	neutral100, primary800, green600, neutralWhite,
	labelWarningIconBg, criticalRedBg, labelInfoBg, grayXLightBg,
} from '../themes/colors';
import React from 'react';
import { withCssSelector } from '../shared/withCssSelector';
import { LabelVariant, LabelVariantCssDefinition } from './LabelVariant';
import { Icon, IconName } from '../icon';

const strongColors: Record<LabelVariant, { bg: string; text: string }> = {
	neutral: { bg: neutral100, text: primary800 },
	success: { bg: green600, text: neutralWhite },
	warning: { bg: labelWarningIconBg, text: neutralWhite },
	critical: { bg: criticalRedBg, text: neutralWhite },
	informational: { bg: labelInfoBg, text: neutralWhite },
	secondary: { bg: grayXLightBg, text: primary800 },
};

const sizeStyles = {
	default: {
		height: '20px',
		padding: '4px 6px',
		fontSize: '12px',
		icon: '14px',
	},
	large: {
		height: '28px',
		padding: '4px 10px',
		fontSize: '14px',
		icon: '16px',
	},
};

const StyledSpan = styled.span<{
	$variant: LabelVariant;
	$rounded?: boolean;
	$darkColor?: boolean;
	$size?: 'default' | 'large';
}>`
	height: ${props => sizeStyles[props.$size || 'default'].height};
	padding: ${props => sizeStyles[props.$size || 'default'].padding};
	border-radius: ${props => props.$rounded ? '18px' : '2px'};
	width: fit-content;
	display: flex;
	align-items: center;
	gap: 4px;

	text-align: center;
	font-size: ${props => sizeStyles[props.$size || 'default'].fontSize};
	font-style: normal;
	font-weight: 400;
	line-height: 16px;

	.icon {
		font-size: ${props => sizeStyles[props.$size || 'default'].icon};
	}

	${props => LabelVariantCssDefinition[props.$variant]}
	${props => props.$darkColor ? `
		background-color: ${strongColors[props.$variant].bg};
		color: ${strongColors[props.$variant].text};
	` : ''}
`;

const defaultIcons: Record<LabelVariant, IconName> = {
	neutral: 'circle-check-outline_minor',
	success: 'circle-check-outline_minor',
	warning: 'warning-new',
	critical: 'exclamation-circle-outline',
	informational: 'info-icon-new',
	secondary: 'info-icon-new',
};

export type LabelProps = {
	variant?: LabelVariant | undefined;
	icon?: IconName;
	rounded?: boolean;
	number?: number;
	useDefaultIcon?: boolean;
	darkColor?: boolean;
	size?: 'default' | 'large';
} & React.ComponentPropsWithoutRef<'span'>

export const Label = withCssSelector(React.forwardRef<HTMLSpanElement, LabelProps>(
	function Label({ variant = 
	'neutral', icon, rounded, number, useDefaultIcon, darkColor, size = 'default', ...props }, ref) {
		// If number is provided, ignore icon
		const displayIcon = number === undefined 
			? (icon || (useDefaultIcon ? defaultIcons[variant] : undefined))
			: undefined;

		return (
			<StyledSpan $variant={variant} $rounded={rounded} $darkColor={darkColor} $size={size} {...props} ref={ref}>
				{displayIcon && <Icon icon={displayIcon} />}
				{number !== undefined ? number : props.children}
			</StyledSpan>
		);
	}
), StyledSpan.toString());
